import { ErrorHandler, Injectable, Injector } from '@angular/core';
import { environment } from 'src/environments/environment';
import { LoggerService } from './logger.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  private logger: LoggerService | null = null;

  constructor(private injector: Injector) {
    this.setupGlobalErrorHandlers();
  }

  private setupGlobalErrorHandlers(): void {
    if (typeof window !== 'undefined') {
      this.setupWindowErrorHandler();
      this.setupUnhandledRejectionHandler();
    }
  }

  private setupWindowErrorHandler(): void {
    try {
      const originalOnError = window.onerror;
      window.onerror = (message: string | Event, source?: string, lineno?: number, colno?: number, error?: Error) => {
        try {
          const errorMessage = typeof message === 'string' ? message : 'Unknown window error';
          this.getLoggerService().error(
            `Window error: ${errorMessage}`,
            'WINDOW_ERROR',
            error,
            {
              source: source,
              lineno: lineno,
              colno: colno,
              errorName: error?.name || 'WindowError'
            }
          );
        } catch {
          // Silently fail if logging fails
        }

        if (originalOnError) {
          return originalOnError(message, source, lineno, colno, error);
        }
        return false;
      };
    } catch {
      // Silently fail if setup fails
    }
  }

  private setupUnhandledRejectionHandler(): void {
    if (window.addEventListener) {
      window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
        try {
          event.preventDefault();
          const reason = event.reason;
          const errorMessage = reason instanceof Error ? reason.message : String(reason || 'Unknown error');
          
          this.getLoggerService().error(
            `Unhandled promise rejection: ${errorMessage}`,
            'UNHANDLED_REJECTION',
            reason instanceof Error ? reason : new Error(errorMessage),
            {
              errorName: reason instanceof Error ? reason.name : 'UnhandledPromiseRejection'
            }
          );
        } catch {
          // Silently fail if logging fails
        }
      });
    }
  }

  private getLoggerService(): LoggerService {
    if (!this.logger) {
      this.logger = this.injector.get(LoggerService);
    }
    return this.logger;
  }

  private extractErrorInfo(error: any): { message: string; stack?: string; metadata?: Record<string, any> } {
    let message = 'Unknown error occurred';
    let stack: string | undefined;
    const metadata: Record<string, any> = {};

    if (error instanceof Error) {
      message = error.message || message;
      stack = error.stack;
      if (error.name) {
        metadata.errorName = error.name;
      }
    } else if (typeof error === 'string') {
      message = error;
    } else if (error && typeof error === 'object') {
      if (error.message) {
        message = String(error.message);
      }
      if (error.stack) {
        stack = String(error.stack);
      }
      if (error.name) {
        metadata.errorName = error.name;
      }
    }

    return { message, stack, metadata: Object.keys(metadata).length > 0 ? metadata : undefined };
  }

  handleError(error: any): void {
    try {
      const errorInfo = this.extractErrorInfo(error);
      const logger = this.getLoggerService();

      let moduleName = 'GlobalErrorHandler';
      if (errorInfo.stack) {
        const stackMatch = errorInfo.stack.match(/at\s+(\w+Component|\w+Service|\w+Directive)/);
        if (stackMatch) {
          moduleName = stackMatch[1];
        }
      }

      logger.error(
        errorInfo.message,
        moduleName,
        errorInfo.stack ? new Error(errorInfo.stack) : undefined,
        errorInfo.metadata
      );

      if (!environment.production) {
        console.error('GlobalErrorHandler caught an error:', error);
      }
    } catch (loggingError) {
      console.error('Error in GlobalErrorHandler:', loggingError);
      console.error('Original error:', error);
    }
  }
}

